import React, { useEffect, useState } from 'react'
import { useAuth } from '../../context/useAuth'
import { useNotificationPreferences } from '../../hooks/useNotificationPreferences'
import { createDefaultUserNotificationPreferences } from '../../services/notificationService'
import type { NotificationCategory, NotificationPreferenceMatrix, UserNotificationPreferences } from '../../types/schema'
import { CATEGORY_GROUPS, CATEGORY_LABELS, formatQuietRange } from '../../utils/notificationCategories'
import { Checkbox, Select, SettingsRow, SettingsSection, Toggle, useInstantSettingRows } from '../Common'
import { useNls } from '../../nls/nlsProvider'
import { DigestPreferencesPanel } from './DigestPreferencesPanel'

const HOURS = Array.from({ length: 24 }, (_, hour) => hour)
const CHANNELS: Array<{ key: keyof NotificationPreferenceMatrix; label: string; phaseTwo?: boolean }> = [
  { key: 'inApp', label: 'In-App' },
  { key: 'push', label: 'Push' },
  { key: 'email', label: 'Email', phaseTwo: true },
  { key: 'digest', label: 'Digest' },
]

type NotificationSettingsProps = {
  /** Member chrome opts into immediate persistence; desktop is now also instant (SR5). */
  memberMobile?: boolean
}

export const NotificationSettings = ({ memberMobile = false }: NotificationSettingsProps) => {
  void memberMobile
  const { t } = useNls()
  const { currentUser } = useAuth()
  const { preferences, savePreferences } = useNotificationPreferences()
  const userId = currentUser?.uid
  const [draft, setDraft] = useState<UserNotificationPreferences | null>(() =>
    preferences
      ? structuredClone(preferences)
      : userId
        ? createDefaultUserNotificationPreferences(userId)
        : null,
  )

  const instantRows = useInstantSettingRows()

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs draft when remote preferences change
    if (preferences) setDraft(structuredClone(preferences))
    else if (userId) setDraft(createDefaultUserNotificationPreferences(userId))
  }, [userId, preferences])

  if (!draft) return <p className="skEmptyText">{t('NLSSettingsNotificationsSignedOut')}</p>

  const save = async (next: UserNotificationPreferences, key: string = 'general') => {
    await instantRows.run(key, async () => {
      await savePreferences(next)
    })
  }

  const updateDraftAndSave = (
    updater: (current: UserNotificationPreferences) => UserNotificationPreferences,
    key?: string,
  ) => {
    const next = updater(draft)
    setDraft(next)
    void save(next, key)
  }

  const setCategory = (category: NotificationCategory, channel: keyof NotificationPreferenceMatrix) => {
    updateDraftAndSave(
      (current) => ({
        ...current,
        categories: {
          ...current.categories,
          [category]: { ...current.categories[category], [channel]: !current.categories[category][channel] },
        },
      }),
      `cat-${category}-${channel}`,
    )
  }

  const setQuiet = <K extends keyof UserNotificationPreferences['quietMode']>(
    key: K,
    value: UserNotificationPreferences['quietMode'][K],
  ) => {
    updateDraftAndSave(
      (current) => ({ ...current, quietMode: { ...current.quietMode, [key]: value } }),
      'quietMode',
    )
  }

  return (
    <div className="skSectionStack">
      {/* 1. Channels Section */}
      <SettingsSection title={t('NLSSettingsSectionChannels')} flush>
        <div className="skMatrix" data-testid="delivery-matrix">
          <table className="skMatrix__table">
            <thead>
              <tr className="skMatrix__headerRow">
                <th className="skMatrix__th skMatrix__th--label">{t('NLSSettingsSectionChannels')}</th>
                {CHANNELS.map((channel) => (
                  <th
                    key={channel.key}
                    className="skMatrix__th"
                    title={channel.phaseTwo ? t('NLSSettingsNotificationEmailComingSoon') : undefined}
                  >
                    {channel.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {CATEGORY_GROUPS.map((group) => (
                <React.Fragment key={group.label}>
                  <tr className="skMatrix__groupRow">
                    <td colSpan={CHANNELS.length + 1} className="skMatrix__groupTitle">
                      {group.label}
                    </td>
                  </tr>
                  {group.categories.map((category) => (
                    <tr key={category} className="skMatrix__row">
                      <td className="skMatrix__cellLabel">
                        {CATEGORY_LABELS[category]}
                      </td>
                      {CHANNELS.map((channel) => (
                        <td
                          key={channel.key}
                          className="skMatrix__cellControl"
                          title={channel.phaseTwo ? t('NLSSettingsNotificationEmailComingSoon') : undefined}
                        >
                          <Checkbox
                            checked={draft.categories[category][channel.key]}
                            aria-label={`${CATEGORY_LABELS[category]} ${channel.label}`}
                            onChange={() => setCategory(category, channel.key)}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </SettingsSection>

      {/* 2. Digest Section */}
      <SettingsSection title={t('NLSSettingsSectionDigest')}>
        <SettingsRow
          label={t('NLSSettingsNotificationsDigestTitle')}
          hint={t('NLSSettingsNotificationsDigestBody')}
          status={instantRows.statusFor('digest')}
          control={
            <Toggle
              checked={draft.digest.enabled}
              onChange={() =>
                updateDraftAndSave(
                  (current) => ({
                    ...current,
                    digest: { enabled: !current.digest.enabled },
                  }),
                  'digest',
                )
              }
              aria-label={t('NLSSettingsNotificationsDigestTitle')}
            />
          }
        />

        <SettingsRow
          label={t('NLSSettingsNotificationsBundleRepeats')}
          hint={t('NLSSettingsNotificationsBundleRepeatsHint')}
          status={instantRows.statusFor('bundleRepeats')}
          control={
            <Toggle
              checked={Boolean(draft.bundleRepeats)}
              onChange={() =>
                updateDraftAndSave(
                  (current) => ({
                    ...current,
                    bundleRepeats: !current.bundleRepeats,
                  }),
                  'bundleRepeats',
                )
              }
              aria-label={t('NLSSettingsNotificationsBundleRepeats')}
            />
          }
        />

        <DigestPreferencesPanel />
      </SettingsSection>

      {/* 3. Quiet Hours Section */}
      <SettingsSection title={t('NLSSettingsSectionQuietHours')}>
        <SettingsRow
          label={t('NLSSettingsNotificationsQuietModeTitle')}
          hint={`${formatQuietRange(draft.quietMode.startHour, draft.quietMode.endHour)} in ${draft.timezone}. ${t('NLSSettingsNotificationsQuietModeHint')}`}
          status={instantRows.statusFor('quietMode')}
          control={
            <Toggle
              checked={draft.quietMode.enabled}
              onChange={() => setQuiet('enabled', !draft.quietMode.enabled)}
              aria-label={t('NLSSettingsNotificationsQuietModeTitle')}
            />
          }
        />

        <SettingsRow
          label={t('NLSSettingsSectionQuietHours')}
          hint={`${t('NLSSettingsNotificationsStarts')}: ${String(draft.quietMode.startHour).padStart(2, '0')}:00 · ${t('NLSSettingsNotificationsEnds')}: ${String(draft.quietMode.endHour).padStart(2, '0')}:00`}
          control={
            <div className="skQuietHoursControls">
              {(['startHour', 'endHour'] as const).map((key) => (
                <label
                  key={key}
                  className="skQuietHoursSelectLabel"
                >
                  <span>{key === 'startHour' ? t('NLSSettingsNotificationsStarts') : t('NLSSettingsNotificationsEnds')}</span>
                  <Select<number>
                    ariaLabel={t('NLSSettingsSectionQuietHours')}
                    value={draft.quietMode[key]}
                    options={HOURS.map((hour) => ({ value: hour, label: `${String(hour).padStart(2, '0')}:00` }))}
                    onChange={(value) => setQuiet(key, value)}
                    trigger="pill"
                  />
                </label>
              ))}
            </div>
          }
        />
      </SettingsSection>
    </div>
  )
}
